import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ClockPage } from "./pages/ClockPage";
import { SchedulePage } from "./pages/SchedulePage";
import { VacationsPage } from "./pages/VacationsPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import { DepartmentsPage } from "./pages/DepartmentsPage";
import { BranchesPage } from "./pages/BranchesPage";
import { FlaggedPage } from "./pages/FlaggedPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ShiftSwapsPage } from "./pages/ShiftSwapsPage";
import { AvailabilityPage } from "./pages/AvailabilityPage";
import { SuperAdminPage } from "./pages/SuperAdminPage";
import { ReportsPage } from "./pages/ReportsPage";
import { LandingPage } from "./pages/LandingPage";
import { PricingPage } from "./pages/PricingPage";
import { FAQPage } from "./pages/FAQPage";
import { Toaster } from "./components/ui/sonner";

const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, loading, hasRole } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !hasRole(allowedRoles)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner" />
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/register"
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/forgot-password"
                element={
                    <PublicRoute>
                        <ForgotPasswordPage />
                    </PublicRoute>
                }
            />
            <Route
                path="/reset-password"
                element={
                    <PublicRoute>
                        <ResetPasswordPage />
                    </PublicRoute>
                }
            />

            {/* Protected Routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/clock"
                element={
                    <ProtectedRoute>
                        <ClockPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/schedule"
                element={
                    <ProtectedRoute>
                        <SchedulePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/vacations"
                element={
                    <ProtectedRoute>
                        <VacationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/employees"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                        <EmployeesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/departments"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager"]}>
                        <DepartmentsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/branches"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager"]}>
                        <BranchesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/flagged"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                        <FlaggedPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reports"
                element={
                    <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                        <ReportsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/shift-swaps"
                element={
                    <ProtectedRoute>
                        <ShiftSwapsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/availability"
                element={
                    <ProtectedRoute>
                        <AvailabilityPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute allowedRoles={["admin"]}>
                        <SettingsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/super-admin"
                element={
                    <ProtectedRoute allowedRoles={["super_admin"]}>
                        <SuperAdminPage />
                    </ProtectedRoute>
                }
            />

            {/* Public Marketing Pages */}
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />

            {/* Default Redirect - Landing page for unauthenticated, dashboard for authenticated */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
